import React, { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { generateAppRoutes, pathTo } from "./utils";

import Button from "../../components/Button";
import LinkButton from "../../components/LinkButton";
import Modal from "../../components/ModalPopup";
import ButtonList from "../../components/ButtonList";
import { fetchAuthSession } from "@aws-amplify/auth";

export const RouteContext = React.createContext({
  breadcrumbs: [],
  currentRoute: null,
  rootRoute: null,
  routes: [],
});

const IDLE_TIMEOUT =
  (parseInt(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES, 10) || 25) * 60 * 1000; // Default: 25 minute
const GRACE_PERIOD =
  (parseInt(process.env.REACT_APP_GRACE_PERIOD_MINUTES, 10) || 5) * 60 * 1000; // Default: 5 minute
const SESSION_CHANNEL_NAME = "session-sync-channel";
const SESSION_STORAGE_KEY = "session-last-activity";
const SESSION_EXTENDED_KEY = "session-extended-timestamp";
const ACTIVE_TAB_KEY = "session-active-tab";
const CHECK_INTERVAL = 1000; // Check every second for more reliable timeout detection

// Generate unique tab ID
const generateTabId = () => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Message types for cross-tab communication
const MESSAGE_TYPES = {
  SESSION_EXTENDED: "SESSION_EXTENDED",
  LOGOUT: "LOGOUT",
  ACTIVITY_DETECTED: "ACTIVITY_DETECTED",
};

const RouteProvider = ({ children = null, value }) => {
  const location = useLocation();
  const [openRefreshModal, setOpenRefreshModal] = React.useState(false);
  const [gracePeriodMinutes, setGracePeriodMinutes] = React.useState(2);

  // Refs for timer management
  const idleTimerRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const graceTimerRef = useRef(null); // Use ref for grace timer to avoid stale closures
  const lastActivityRef = useRef(Date.now());
  const broadcastChannelRef = useRef(null);
  const isIdleRef = useRef(false);
  const triggerIdlePromptRef = useRef(null);
  const tabIdRef = useRef(generateTabId());
  const graceStartTimeRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Check if this tab is the active tab
  const isActiveTab = useCallback(() => {
    const activeTabId = window.localStorage.getItem(ACTIVE_TAB_KEY);
    return activeTabId === tabIdRef.current;
  }, []);

  // Claim this tab as the active tab
  const claimActiveTab = useCallback(() => {
    window.localStorage.setItem(ACTIVE_TAB_KEY, tabIdRef.current);
  }, []);

  // Broadcast message to all tabs
  const broadcastMessage = useCallback((type, data = {}) => {
    const message = { type, data, timestamp: Date.now() };

    // Use BroadcastChannel if available
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage(message);
      } catch (e) {
        console.warn("BroadcastChannel postMessage failed:", e);
      }
    }

    // Also use localStorage as fallback for cross-tab sync
    try {
      window.localStorage.setItem(
        SESSION_EXTENDED_KEY,
        JSON.stringify(message),
      );
    } catch (e) {
      console.warn("localStorage setItem failed:", e);
    }
  }, []);

  // Handle logout across all tabs
  const handleLogout = useCallback(() => {
    console.log("Logging out due to inactivity...");
    broadcastMessage(MESSAGE_TYPES.LOGOUT);
    window.localStorage.clear();
    setOpenRefreshModal(false);
    window.location.href = "/login";
  }, [broadcastMessage]);

  // Trigger the idle prompt modal - ONLY on the active tab
  const triggerIdlePrompt = useCallback(() => {
    if (location.pathname === "/login") {
      return;
    }

    // Use ref to check if already in grace period (more reliable than React state)
    if (graceStartTimeRef.current) {
      return;
    }

    // Only show popup on the last active tab
    if (!isActiveTab()) {
      return;
    }

    isIdleRef.current = true;
    const graceStartTime = Date.now();
    graceStartTimeRef.current = graceStartTime;

    const graceMinutes = Math.ceil(GRACE_PERIOD / 60000);
    setGracePeriodMinutes(graceMinutes);
    setOpenRefreshModal(true);

    // Store grace start time in localStorage so other tabs know
    window.localStorage.setItem(
      "session-grace-start",
      graceStartTime.toString(),
    );

    // Note: We don't rely solely on setTimeout anymore
    // The interval check will handle logout for reliability
    graceTimerRef.current = setTimeout(() => {
      handleLogout();
    }, GRACE_PERIOD);
  }, [location.pathname, handleLogout, isActiveTab]);

  // Keep ref updated for use in setTimeout callbacks
  useEffect(() => {
    triggerIdlePromptRef.current = triggerIdlePrompt;
  }, [triggerIdlePrompt]);

  // Reset all timers and update activity timestamp
  const resetTimers = useCallback(() => {
    // Clear existing idle timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    // Clear grace period timer (use ref for reliable access)
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }

    // Update last activity
    lastActivityRef.current = Date.now();
    isIdleRef.current = false;
    graceStartTimeRef.current = null;

    // Store in localStorage for cross-tab sync
    window.localStorage.setItem(SESSION_STORAGE_KEY, Date.now().toString());
    window.localStorage.removeItem("session-grace-start");

    // Claim this tab as active since user is interacting here
    claimActiveTab();

    // Start new idle timer if not on login page
    if (location.pathname !== "/login") {
      idleTimerRef.current = setTimeout(() => {
        if (location.pathname !== "/login" && triggerIdlePromptRef.current) {
          triggerIdlePromptRef.current();
        }
      }, IDLE_TIMEOUT);
    }
  }, [location.pathname, claimActiveTab]);

  // Handle activity events
  const handleActivity = useCallback(() => {
    if (location.pathname === "/login") return;

    // Claim this tab as active since user is interacting here
    claimActiveTab();

    // Only reset if not already in grace period (use refs for reliable check)
    // Don't use React state here - it causes stale closures
    if (!isIdleRef.current && !graceStartTimeRef.current) {
      resetTimers();
      // Broadcast activity to other tabs so they can sync their timers
      broadcastMessage(MESSAGE_TYPES.ACTIVITY_DETECTED, {
        timestamp: Date.now(),
      });
    }
  }, [location.pathname, resetTimers, claimActiveTab, broadcastMessage]);

  // Handle messages from other tabs
  const handleCrossTabMessage = useCallback(
    (message) => {
      if (!message || !message.type) return;

      console.log("[Cross-Tab]", message.type);

      switch (message.type) {
        case MESSAGE_TYPES.SESSION_EXTENDED:
          // Another tab extended the session - reset our timers and close modal
          if (graceTimerRef.current) {
            clearTimeout(graceTimerRef.current);
            graceTimerRef.current = null;
          }
          setOpenRefreshModal(false);
          isIdleRef.current = false;
          graceStartTimeRef.current = null;
          lastActivityRef.current = message.timestamp || Date.now();

          // Also update localStorage to sync activity timestamp
          window.localStorage.setItem(
            SESSION_STORAGE_KEY,
            (message.timestamp || Date.now()).toString(),
          );
          window.localStorage.removeItem("session-grace-start");

          // Restart idle timer
          if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
          }
          if (location.pathname !== "/login") {
            idleTimerRef.current = setTimeout(() => {
              if (
                location.pathname !== "/login" &&
                triggerIdlePromptRef.current
              ) {
                triggerIdlePromptRef.current();
              }
            }, IDLE_TIMEOUT);
          }
          break;

        case MESSAGE_TYPES.LOGOUT:
          // Another tab triggered logout
          window.localStorage.clear();
          setOpenRefreshModal(false);
          window.location.href = "/login";
          break;

        case MESSAGE_TYPES.ACTIVITY_DETECTED:
          // Another tab detected activity - sync our timer
          // Use refs instead of state for reliable check
          if (!isIdleRef.current && !graceStartTimeRef.current) {
            const activityTime = message.timestamp || Date.now();
            lastActivityRef.current = activityTime;
            // Also update localStorage so interval check sees it
            window.localStorage.setItem(
              SESSION_STORAGE_KEY,
              activityTime.toString(),
            );

            // Restart idle timer to match the active tab
            if (idleTimerRef.current) {
              clearTimeout(idleTimerRef.current);
            }
            if (location.pathname !== "/login") {
              idleTimerRef.current = setTimeout(() => {
                if (
                  location.pathname !== "/login" &&
                  triggerIdlePromptRef.current
                ) {
                  triggerIdlePromptRef.current();
                }
              }, IDLE_TIMEOUT);
            }
          }
          break;

        default:
          break;
      }
    },
    [location.pathname],
  );

  // Initialize BroadcastChannel and localStorage listener
  useEffect(() => {
    // Setup BroadcastChannel for modern browsers
    if (typeof BroadcastChannel !== "undefined") {
      broadcastChannelRef.current = new BroadcastChannel(SESSION_CHANNEL_NAME);
      broadcastChannelRef.current.onmessage = (event) => {
        handleCrossTabMessage(event.data);
      };
    }

    // Setup localStorage listener as fallback (for Safari and older browsers)
    const handleStorageChange = (event) => {
      if (event.key === SESSION_EXTENDED_KEY && event.newValue) {
        try {
          const message = JSON.parse(event.newValue);
          handleCrossTabMessage(message);
        } catch (e) {
          console.warn("Failed to parse storage event:", e);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [handleCrossTabMessage]);

  // Setup activity listeners
  useEffect(() => {
    if (location.pathname === "/login") {
      // Clear timers on login page
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    const events = [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
    ];

    // Throttle activity detection
    let lastEventTime = 0;
    const throttleMs = 500;

    const throttledActivity = (event) => {
      const now = Date.now();
      if (now - lastEventTime > throttleMs) {
        lastEventTime = now;
        handleActivity();
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, throttledActivity, { passive: true });
    });

    // Start initial idle timer ONLY on first mount
    // Never reset timers on subsequent effect re-runs
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;

      // Check if we already have a recent activity in localStorage
      const lastActivity = parseInt(
        window.localStorage.getItem(SESSION_STORAGE_KEY) || "0",
      );
      const timeSinceLastActivity = Date.now() - lastActivity;

      // If no activity recorded yet (fresh session), initialize it
      if (lastActivity === 0) {
        resetTimers();
      } else if (timeSinceLastActivity < IDLE_TIMEOUT) {
        // Recent activity exists - just start timer for remaining time
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
        }
        const remainingTime = IDLE_TIMEOUT - timeSinceLastActivity;
        idleTimerRef.current = setTimeout(() => {
          if (location.pathname !== "/login" && triggerIdlePromptRef.current) {
            triggerIdlePromptRef.current();
          }
        }, remainingTime);
      }
      // If timeSinceLastActivity >= IDLE_TIMEOUT, the interval check will handle showing the modal
    }

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledActivity);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
    // Note: resetTimers is intentionally NOT in dependencies to prevent re-initialization
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, handleActivity]);

  // Monitor tab visibility for detecting sleep/wake and claiming active tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // This tab is now visible, claim it as active
        claimActiveTab();

        // Skip check on login page
        if (location.pathname === "/login") return;

        // Check if we should still be logged in by reading localStorage
        const lastExtended = window.localStorage.getItem(SESSION_EXTENDED_KEY);
        if (lastExtended) {
          try {
            const message = JSON.parse(lastExtended);
            const timeSinceExtended = Date.now() - message.timestamp;

            // If session was extended recently, sync our state
            if (
              message.type === MESSAGE_TYPES.SESSION_EXTENDED &&
              timeSinceExtended < IDLE_TIMEOUT
            ) {
              handleCrossTabMessage(message);
              return;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }

        // Check if there's an ongoing grace period from another tab
        const graceStartStr = window.localStorage.getItem(
          "session-grace-start",
        );
        const graceStart = parseInt(graceStartStr || "0");
        if (graceStart > 0) {
          const elapsedGrace = Date.now() - graceStart;
          if (elapsedGrace >= GRACE_PERIOD) {
            // Grace period already expired, logout
            handleLogout();
            return;
          }
          // Grace period ongoing - sync our ref and show modal if not showing
          if (!graceStartTimeRef.current) {
            graceStartTimeRef.current = graceStart;
            isIdleRef.current = true;
            setOpenRefreshModal(true);
            setGracePeriodMinutes(Math.ceil(GRACE_PERIOD / 60000));
          }
          return;
        }

        // Check if user has been inactive too long
        // Use ONLY localStorage for activity tracking (updated only on real user activity)
        const lastActivity = parseInt(
          window.localStorage.getItem(SESSION_STORAGE_KEY) || "0",
        );
        const elapsedTime = Date.now() - lastActivity;

        if (elapsedTime >= IDLE_TIMEOUT + GRACE_PERIOD) {
          // Total inactivity exceeded, logout immediately
          handleLogout();
        } else if (elapsedTime >= IDLE_TIMEOUT && !graceStartTimeRef.current) {
          // Idle timeout exceeded - this tab just became active, so show the prompt here
          triggerIdlePrompt();
        }
      }
    };

    // Also handle focus events to claim active tab
    const handleFocus = () => {
      claimActiveTab();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [
    location.pathname,
    handleLogout,
    triggerIdlePrompt,
    handleCrossTabMessage,
    claimActiveTab,
  ]);

  // CRITICAL: Interval-based check for timeout detection
  // This runs every second and ensures the ACTIVE tab triggers logout reliably
  // (setTimeout can be throttled in background tabs, but setInterval in foreground is reliable)
  useEffect(() => {
    if (location.pathname === "/login") {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Claim as active tab on mount
    claimActiveTab();

    checkIntervalRef.current = setInterval(() => {
      // Only the active (visible) tab should handle timeout logic
      if (document.hidden) {
        return; // Skip check if tab is hidden
      }

      const now = Date.now();
      // Use ONLY localStorage for activity tracking (shared across tabs, updated only on real user activity)
      const lastActivity = parseInt(
        window.localStorage.getItem(SESSION_STORAGE_KEY) || "0",
      );
      const elapsedSinceActivity = now - lastActivity;

      // CRITICAL: Check total elapsed time - if beyond idle + grace, logout immediately
      if (elapsedSinceActivity >= IDLE_TIMEOUT + GRACE_PERIOD) {
        handleLogout();
        return;
      }

      // Check if grace period has started (modal should be showing)
      const graceStartStr = window.localStorage.getItem("session-grace-start");
      const graceStart = parseInt(graceStartStr || "0");

      if (graceStart > 0) {
        const elapsedGrace = now - graceStart;
        if (elapsedGrace >= GRACE_PERIOD) {
          handleLogout();
          return;
        }
        // Grace period is active but not expired yet, skip showing prompt again
        return;
      }

      // Check if idle timeout has been reached - show popup if this is active tab
      if (elapsedSinceActivity >= IDLE_TIMEOUT) {
        // Only the active tab shows the popup
        if (isActiveTab()) {
          if (triggerIdlePromptRef.current) {
            triggerIdlePromptRef.current();
          }
        }
      }
    }, CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
    // Note: openRefreshModal is intentionally NOT in the dependency array
    // to prevent the interval from being recreated when the modal opens/closes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, handleLogout, claimActiveTab, isActiveTab]);

  // Extend session and broadcast to all tabs
  const refreshToken = async () => {
    // FIRST: Clear grace period refs BEFORE anything else
    // This prevents any interval checks from seeing stale state
    isIdleRef.current = false;
    graceStartTimeRef.current = null;

    // Clear localStorage grace start immediately
    window.localStorage.removeItem("session-grace-start");

    // Close modal and clear grace timer
    setOpenRefreshModal(false);
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }

    // Update activity timestamp
    const now = Date.now();
    lastActivityRef.current = now;
    window.localStorage.setItem(SESSION_STORAGE_KEY, now.toString());

    // CRITICAL: Broadcast session extension to ALL tabs
    broadcastMessage(MESSAGE_TYPES.SESSION_EXTENDED, { timestamp: now });

    // Clear and restart idle timer (don't call full resetTimers to avoid redundant operations)
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (location.pathname !== "/login") {
      idleTimerRef.current = setTimeout(() => {
        if (location.pathname !== "/login" && triggerIdlePromptRef.current) {
          triggerIdlePromptRef.current();
        }
      }, IDLE_TIMEOUT);
    }

    try {
      await fetchAuthSession();
      window.localStorage.setItem("SESSION_LOGIN_TIME", Date.now().toString());
      console.log("Session refreshed successfully");
    } catch (error) {
      console.error("Session refresh failed, logging out...", error);
      handleLogout();
    }
  };

  const routes = generateAppRoutes(value);
  const currentRoute = routes.find((route) => route.path === location.pathname);
  const rootRoute = routes.find((route) => !route.parent);

  const breadcrumbs = currentRoute ? pathTo(currentRoute) : [];
  const context = { breadcrumbs, currentRoute, rootRoute, routes };

  const refreshConfirmModal = () => {
    return (
      <Modal open={openRefreshModal} onClose={() => setOpenRefreshModal(false)}>
        <h3>
          {" "}
          <LinkButton
            icon="overlay_icon"
            style={{
              fontSize: "5rem",
              float: "left",
              color: "grey",
              cursor: "none",
              outline: "none",
            }}
          />
          Your session is about to expire!
        </h3>
        <br />
        <div>
          You will be logged out in {gracePeriodMinutes}{" "}
          {gracePeriodMinutes === 1 ? "minute" : "minutes"} if you don't take
          action.
        </div>
        <div>Do you want to stay signed in?</div>
        <br />
        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button primary onClick={refreshToken}>
              Yes, Keep me signed in
            </Button>
            <Button
              onClick={() => {
                // Just close the modal, don't reset - let the grace timer continue
                setOpenRefreshModal(false);
              }}
            >
              Close
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  return (
    <RouteContext.Provider value={context}>
      {typeof children === "function" ? children(context) : children}
      {refreshConfirmModal()}
    </RouteContext.Provider>
  );
};

export default RouteProvider;
